/**
 * The painter's open water beyond its grazing ramp (STYLE §7), mirrored by openWater.glsl: a plane
 * of lightness and b* across the IJ, and patches of lightness and colour on it.
 *
 * Measured on the museum's scan area-averaged to the 2000 px frame, on the IJ's water more than 40 m
 * from any shore, vessels and 3 px about them left out by colour (a* above -6, L* below 20 or above
 * 52, after a 1 px blur), against a painter-view render whose open water carries openWater.ts's ramp:
 * - the plane: what the ramp leaves, fitted by least squares in layout metres. The painting's IJ is
 *   lighter and less yellow toward the north and east of the harbour. It explains 6% of the
 *   lightness left and 15% of the b* left; a* shows none (0.04%), so it takes none.
 * - the patches: about the grazing-bin medians and the plane, after a Gaussian of 3 px, the
 *   painting's water spreads 2.05 L*, 1.37 a* and 2.23 b* (robust standard deviations, from the
 *   quartiles), where the render's spreads 0.78, 0.21 and 0.39; the patches add the difference in
 *   quadrature. a* and b* move together (correlation 0.43 on the central 90%), lightness with
 *   neither (-0.10, -0.05). Their autocorrelation falls to 1/e over 33 m in lightness, 23 m in b*
 *   and 12 m in a* (geometric mean of the frame's two axes, through the painter rays' 0.65 and
 *   0.97 m a pixel on the water).
 * Each channel's patches are the water brush's value noise (brush.glsl) on a cell that gives its
 * length, scaled to its spread; a* takes b*'s noise in its correlation's share.
 */
import type { Lab } from '../core/color';
import { clamp } from '../core/math';
import type { Vec2 } from '../geo/types';
import { hash12 } from './patterns/pavingStones';

/** The value noise of brush.glsl, remapped to [-1, 1]: its robust standard deviation and the
 *  distance, in cells, over which its autocorrelation falls to 1/e (openWaterPatches.test.ts
 *  measures both on this mirror). */
export const VALUE_NOISE = {
  robustSd: 0.4855,
  efoldCells: 0.773,
} as const;

export const OPEN_WATER_PLANE = {
  /** Layout metres: the mean of the fitted water, where the plane is 0. */
  centreM: [616.2, 567.8],
  /** The fitted water's 1st to 99th percentiles; beyond them the plane holds its edge value. */
  rangeXM: [146.1, 1194.4],
  rangeYM: [-138.0, 1040.8],
  /** L* and b* a metre east (x) and north (y). */
  lightnessPerM: [1.247 / 300, 1.584 / 300],
  bPerM: [-0.1935 / 300, -1.394 / 300],
} as const;

export const OPEN_WATER_PATCHES = {
  /** Robust standard deviations the patches add, in L*, a* and b*. */
  spread: [1.9, 1.35, 2.2],
  /** Their 1/e lengths in metres, in L*, a* and b*. */
  lengthM: [33.1, 11.9, 23.0],
  abCorrelation: 0.43,
  /** Where each channel's noise lies on the lattice, so the three are independent. */
  offsets: [
    [0, 0],
    [417.3, 191.7],
    [-263.1, 529.9],
  ],
} as const;

export type Channel = 0 | 1 | 2;

/** brush.glsl valueNoise. */
export function valueNoise(p: Vec2): number {
  const [ix, iy] = [Math.floor(p[0]), Math.floor(p[1])];
  const fade = (t: number): number => t * t * (3 - 2 * t);
  const [fx, fy] = [fade(p[0] - ix), fade(p[1] - iy)];
  const a = hash12([ix, iy]);
  const b = hash12([ix + 1, iy]);
  const c = hash12([ix, iy + 1]);
  const d = hash12([ix + 1, iy + 1]);
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}

/** The cell, in metres, that gives a channel's patches their 1/e length. */
export function patchCellM(channel: Channel): number {
  return OPEN_WATER_PATCHES.lengthM[channel] / VALUE_NOISE.efoldCells;
}

/** A channel's own noise at a layout point, of robust standard deviation 1. */
export function patchNoise(plane: Vec2, channel: Channel): number {
  const cell = patchCellM(channel);
  const offset = OPEN_WATER_PATCHES.offsets[channel];
  const v = valueNoise([plane[0] / cell + offset[0], plane[1] / cell + offset[1]]);
  return (2 * v - 1) / VALUE_NOISE.robustSd;
}

/** The plane's L* and b* at a layout point. */
export function openWaterPlane(plane: Vec2): readonly [number, number] {
  const p = OPEN_WATER_PLANE;
  const dx = clamp(plane[0], p.rangeXM[0], p.rangeXM[1]) - p.centreM[0];
  const dy = clamp(plane[1], p.rangeYM[0], p.rangeYM[1]) - p.centreM[1];
  return [p.lightnessPerM[0] * dx + p.lightnessPerM[1] * dy, p.bPerM[0] * dx + p.bPerM[1] * dy];
}

/** The shift in Lab the painter view lays on its open water at a layout point. */
export function openWaterShift(plane: Vec2): Lab {
  const { spread, abCorrelation } = OPEN_WATER_PATCHES;
  const [lightness, b] = openWaterPlane(plane);
  const nb = patchNoise(plane, 2);
  const na = abCorrelation * nb + Math.sqrt(1 - abCorrelation ** 2) * patchNoise(plane, 1);
  return [lightness + spread[0] * patchNoise(plane, 0), spread[1] * na, b + spread[2] * nb];
}
